
import type { PropType, SetupContext } from 'vue'
import type { SelectOption } from '../../facets/epb-select-option.component.vue'
import { computed, defineComponent, nextTick, ref, watch } from 'vue'
import { EpbSelect } from '../epb-select'

export default defineComponent({
  name: 'EpbSelectHierarchical',
  components: {
    EpbSelect,
  },
  props: {
    options: {
      type: Array as PropType<Array<SelectOption>>,
      default: () => [],
    },
    value: {
      type: Array as PropType<Array<SelectOption>>,
      default: () => [],
    },
    label: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    help: {
      type: String,
      default: () => '',
    },
    loading: Boolean,
    disabled: Boolean,
    hasSearch: Boolean,
    required: Boolean,
    scrollToLastSelectedOptionWhenUpdatingOptions: {
      type: Boolean,
      default: () => true,
    },
    showDropdownWhileLoading: Boolean,
  },

  setup(props, ctx: SetupContext) {
    const baseClass = 'epb-select-hierarchical'
    const { emit } = ctx
    const optionRef = ref<HTMLElement | null>(null)
    const selectedOptions = ref(props.value)
    const multipleDelimiter = ' > '

    const epbSelectValue = computed(() => selectedOptions.value.map(option => option.value))

    function onSelectChange(value: Array<SelectOption['value']>): void {
      const newSelectedOptions = props.options.filter(option => value.includes(option.value))
      setSelectedOptions(computeNewSelectedOptions(props.options, props.value, newSelectedOptions))
      emit('change', selectedOptions.value.length ? selectedOptions.value : [])
    }

    function setSelectedOptions(newSelectedOptions: SelectOption[]): void {
      selectedOptions.value = newSelectedOptions
    }

    function computeNewSelectedOptions(
      allOptions: SelectOption[],
      currentSelectedOptions: SelectOption[],
      newSelectedOptions: SelectOption[],
    ): SelectOption[] {
      // All options were unselected
      if (newSelectedOptions.length === 0) {
        return []
      }

      const newSelectedOption = getIntersectingOption(currentSelectedOptions, newSelectedOptions)
      // A different option was selected
      if (newSelectedOption) {
        return [...findAncestors(allOptions, newSelectedOption), newSelectedOption]
      }

      // A selected option was unselected
      const unselectedOption = getIntersectingOption(newSelectedOptions, currentSelectedOptions)
      return findAncestors(allOptions, unselectedOption)
    }

    function getIntersectingOption(
      options: SelectOption[],
      newOptions: SelectOption[],
    ): SelectOption | undefined {
      return newOptions.find(newOption => !options.find(option => option.value === newOption.value))
    }

    function findAncestors(
      allOptions: SelectOption[],
      selectedOption: SelectOption,
    ): SelectOption[] {
      const ancestors = []
      let currentLevel = selectedOption.level
      for (let i = allOptions.indexOf(selectedOption) - 1; i >= 0; i--) {
        const currentOption = allOptions[i]

        if (currentOption.level < currentLevel) {
          ancestors.push(currentOption)
          currentLevel = currentOption.level
        }

        if (currentOption.level === 0) {
          break
        }
      }

      return ancestors.reverse()
    }

    watch(() => props.value, handleValueChange, { deep: true })

    function handleValueChange(newSelectedOptions: SelectOption[]): void {
      setSelectedOptions(newSelectedOptions)
    }

    watch(() => props.options, scrollToLastSelectedOption, { deep: true })

    async function scrollToLastSelectedOption(): Promise<void> {
      // Wait for the view to refresh
      await nextTick()

      const selectedOptionButtonElements = document.querySelectorAll(
        '.epb-select-dropdown-option__option--selected',
      )
      if (
        selectedOptionButtonElements.length &&
        props.scrollToLastSelectedOptionWhenUpdatingOptions
      ) {
        Array.from(selectedOptionButtonElements)[
          selectedOptionButtonElements.length - 1
        ].scrollIntoView()
      }
    }

    return {
      baseClass,
      epbSelectValue,
      optionRef,
      onSelectChange,
      multipleDelimiter,
    }
  },
})
